import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import AppraisalFooter from '../../../../components/custom_components/Appraisal/AppraisalFooter'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import { useTargetsModalStore } from '../../../../components/custom_components/TargetsModal/TargetsModalStore'
import useManagerMeetingStepTargets from '../../../../graphql/appraisal-flow/hooks/useManagerMeetingStepTargets'
import {
  BonusBetweenAnnualBonusAndProrataDto,
  GetManagerMeetingStepTargetsQuery,
  MeetingStepStatus,
  MeetingStepViewId,
  MeetingType,
} from '../../../../types'
import { useFindPastYearFlexibleBonusByEmployeeIdAndMeetingId } from '../shared/appraisal-components/useBonusAmountEditableIsEnabled'
import { getFlexibleBonusUnknownCurrencyForConversion, toEligibleFlexibleBonusOverride } from '../shared/flexibleBonusUtil'
import { ClosedTargetsComponent } from '../../okr/closedTargets/ClosedTargetsComponent'
import { ManagerExistingObjectives } from '../../okr/ManagerExistingObjectives'
import { MeetingStepDefaultProps } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import AppraisalPageDescription from '../shared/AppraisalPageDescription'
import { PastTargetsFooter } from '../shared/PastTargetsFooter'
import { calculatePastTargetsData } from '../shared/util'
import { useCurrency } from '../../../../context/currency-context'
import { retrieveContractStartAndEndDateFromContract } from '../../../profile/contract/utils/contractUtil'
import { OutdatedTargetsComponent } from '../../okr/oldActiveTargets/OutdatedTargetsComponent'
import { Divider } from 'antd'

interface ManagerMeetingStepPastTargetsContentProps {
  data: GetManagerMeetingStepTargetsQuery
  pastYearFlexibleBonus?: BonusBetweenAnnualBonusAndProrataDto | null
}

export const ManagerMeetingStepPastTargets: FC<MeetingStepDefaultProps> = ({ backBtn, nextBtn, meeting }) => {
  const { data, loading, error } = useManagerMeetingStepTargets(
    meeting.employee.id,
    meeting.id,
    meeting.targetsCalculationHelper.pastTargetsReferenceYear,
    meeting.employee.currentContract!.legalEntity.country,
    true,
    true,
  )

  const isAppraisal = meeting.type === MeetingType.Appraisal
  const {
    data: pastYearFlexibleBonusData,
    loading: pastYearFlexibleBonusLoading,
    error: pastYearFlexibleBonusError,
  } = useFindPastYearFlexibleBonusByEmployeeIdAndMeetingId(meeting.employee.id, meeting.id, !isAppraisal)

  if (
    loading ||
    error ||
    !data ||
    !data.currentContract ||
    pastYearFlexibleBonusError ||
    (isAppraisal && pastYearFlexibleBonusLoading)
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading || pastYearFlexibleBonusLoading}
        error={error || pastYearFlexibleBonusError}
        hasNoData={!data || !data.currentContract}
      />
    )
  } else {
    return (
      <ManagerMeetingStepPastTargetsContent
        data={data}
        meeting={meeting}
        backBtn={backBtn}
        nextBtn={nextBtn}
        pastYearFlexibleBonus={pastYearFlexibleBonusData?.findPastYearFlexibleBonusByEmployeeIdAndMeetingId}
      />
    )
  }
}

const ManagerMeetingStepPastTargetsContent: FC<MeetingStepDefaultProps & ManagerMeetingStepPastTargetsContentProps> = ({
  backBtn,
  nextBtn,
  meeting,
  data,
  pastYearFlexibleBonus,
}) => {
  const [t] = useTranslation()
  const [setModalProps, cleanModalProps] = useTargetsModalStore((state) => [state.setModalProps, state.cleanModalProps], shallow)
  const { currencyConversionData } = useCurrency()

  const isAppraisalUpdate = meeting.type === MeetingType.AppraisalUpdate
  const { pastTargetsReferenceYear } = meeting.targetsCalculationHelper
  const {
    objectivesInMeeting,
    targetBonusOfYear,
    currentContract,
    salaryOfYear,
    branchOfficeByEmployee,
    contractChangesByEmployee,
    isBonusValidationEnabled,
    contractsInYear,
  } = data

  const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(currentContract)
  const pastYearFlexibleBonusOverride = toEligibleFlexibleBonusOverride(
    pastYearFlexibleBonus,
    currentContract!.currencyForPayments,
    currencyConversionData,
    branchOfficeByEmployee.timezone,
    pastTargetsReferenceYear,
    contractsInYear,
    targetBonusOfYear,
    contractChangesByEmployee,
  )
  const flexibleBonusUnknownCurrency = getFlexibleBonusUnknownCurrencyForConversion(
    pastYearFlexibleBonus,
    currentContract!.currencyForPayments,
    currencyConversionData,
  )
  const { openTargetBonus, targetBonusAmount, outdatedTargets } = calculatePastTargetsData(
    branchOfficeByEmployee.timezone,
    isBonusValidationEnabled,
    targetBonusOfYear,
    salaryOfYear,
    currentContract!.annualSalary ? currentContract!.annualSalary.amount : 0,
    currentContract!.targetBonus ? currentContract!.targetBonus.amount : 0,
    contractChangesByEmployee || [],
    objectivesInMeeting,
    currencyConversionData,
    currentContract!.currencyForPayments,
    pastTargetsReferenceYear,
    contractStartDate,
    contractEndDate,
    contractsInYear,
    pastYearFlexibleBonusOverride,
    flexibleBonusUnknownCurrency,
  )

  useEffect(() => {
    setModalProps({
      employeeId: meeting.employee.id,
      meetingId: meeting.id,
      meeting,
      isAppraisalUpdate,
      targetBonus: targetBonusAmount,
      alreadyPlanned: openTargetBonus,
      employeeCurrency: currentContract ? currentContract.currencyForPayments : null,
      employeeCountry: meeting.employee.currentContract ? meeting.employee.currentContract.legalEntity.country : null,
      allowedPastTargets: meeting.allowedMonetaryTargets.pastTargetsAllowed && openTargetBonus > 0,
      disabledMonetaryButtonTooltip:
        openTargetBonus <= 0
          ? t('appraisalMeeting.stepTargetAndBonus.disabledMonetaryButton.targetBonusAlreadyDistributed')
          : !meeting.allowedMonetaryTargets.pastTargetsAllowed
          ? t('appraisalMeeting.stepTargetAndBonus.disabledMonetaryButton.targetsNotAllowedForLowEmployee')
          : '',
    })
    return () => {
      cleanModalProps()
    }
  }, [isAppraisalUpdate, meeting, setModalProps, targetBonusAmount, openTargetBonus, cleanModalProps, currentContract, t])

  return (
    <div data-e2e-manager-meeting-step-past-targets>
      <MeetingBackground withFooter>
        <AppraisalPageDescription description={t('appraisalMeeting.stepTargetAndBonus.pageDescriptionManager')} />
        {!!outdatedTargets.length && (
          <>
            <OutdatedTargetsComponent outdatedTargets={outdatedTargets} employee={meeting.employee} meetingId={meeting.id} />
            <Divider />
          </>
        )}
        <ManagerExistingObjectives
          meeting={meeting}
          objectives={objectivesInMeeting}
          targetBonusOfYear={targetBonusOfYear}
          salaryOfYear={salaryOfYear}
          currentSalary={currentContract!.annualSalary ? currentContract!.annualSalary.amount : 0}
          currentBonus={currentContract!.targetBonus ? currentContract!.targetBonus.amount : 0}
          isPreparationStep={true}
          contractStartDate={contractStartDate}
          contractEndDate={contractEndDate}
          employeeTimezone={branchOfficeByEmployee.timezone}
          isBonusValidationEnabled={isBonusValidationEnabled}
          contractChangesByEmployee={contractChangesByEmployee}
          contractsInYear={contractsInYear}
          pastYearFlexibleBonusOverride={pastYearFlexibleBonusOverride}
          flexibleBonusUnknownCurrency={flexibleBonusUnknownCurrency}
        />

        <ClosedTargetsComponent objectives={objectivesInMeeting} />
      </MeetingBackground>

      <AppraisalFooter>
        <PastTargetsFooter
          objectives={objectivesInMeeting}
          targetBonusOfYear={targetBonusOfYear}
          salaryOfYear={salaryOfYear}
          currentSalary={currentContract!.annualSalary ? currentContract!.annualSalary.amount : 0}
          currentBonus={currentContract!.targetBonus ? currentContract!.targetBonus.amount : 0}
          referenceYear={pastTargetsReferenceYear}
          contractStartDate={contractStartDate}
          contractEndDate={contractEndDate}
          employeeTimezone={branchOfficeByEmployee.timezone}
          isBonusValidationEnabled={isBonusValidationEnabled}
          contractChangesByEmployee={contractChangesByEmployee}
          contractsInYear={contractsInYear}
          pastYearFlexibleBonusOverride={pastYearFlexibleBonusOverride}
          flexibleBonusUnknownCurrency={flexibleBonusUnknownCurrency}
        />
      </AppraisalFooter>
      <PerFlexRow justify="space-between">
        {backBtn && backBtn()}
        {nextBtn && nextBtn(MeetingStepStatus.Done)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.PastTargets} stepStatus={MeetingStepStatus.Done} />
    </div>
  )
}
